import React, { forwardRef } from 'react'
import { NumericFormat, NumericFormatProps } from 'react-number-format'
import * as R from 'ramda'

export interface MoneyInputProps
  extends Omit<
  NumericFormatProps,
  'thousandSeparator' | 'defaultValue' | 'value'
  > {
  readonly defaultValue?: React.HTMLAttributes<HTMLInputElement>['defaultValue']
  readonly thousandSeparator?: string
  readonly value?: number | null
}

/*
  Money input with precision of 2 digits
  5 -> 5.00
  5.05 -> 5.05
  5.45 -> 5.45
*/
const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(
  function MoneyInput(
    {
      onChange,
      defaultValue,
      thousandSeparator = '',
      min,
      max,
      value,
      ...rest
    },
    ref,
  ) {
    return (
      <NumericFormat
        {...rest}
        fixedDecimalScale
        allowNegative={false}
        decimalScale={2}
        // see https://github.com/s-yadav/react-number-format/issues/446
        // @ts-ignore
        defaultValue={defaultValue}
        getInputRef={ref}
        isAllowed={({ floatValue = 0 }) => {
          const satisfiesMin = R.isNil(min) || floatValue >= min
          const satisfiesMax = R.isNil(max) || floatValue <= max

          return satisfiesMin && satisfiesMax
        }}
        thousandSeparator={thousandSeparator}
        /*
        Value type has to be number to be correctly formatted
        with passed decimalSeparator prop and value with 0.00 format
      */
        value={Number(value)}
        onValueChange={(values, sourceInfo) => {
          const { event } = sourceInfo
          if (onChange && event) {
            const newEvent = {
              ...event,
              target: {
                ...event.target,
                value: values.value,
              },
            } as React.ChangeEvent<HTMLInputElement>
            onChange(newEvent)
          }
        }}
      />
    )
  },
)

export default MoneyInput
